/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef, type Ref } from 'react';

import { keyframes } from '@compiled/react';

import { cssMap, jsx } from '@atlaskit/css';
import { Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { RovoLogo } from './components/RovoLogo';
import { RovoLogoRefreshed } from './components/RovoLogoRefreshed';

const ANIMATION_DURATION = 0.65;

const buttonAnimation = keyframes({
	'0%': { transform: 'rotateZ(0)' },
	'40%': { transform: 'rotateZ(-6.74deg)' },
	'60%': { transform: 'rotateZ(6.74deg)' },
	'80%': { transform: 'rotateZ(-6.74deg)' },
	'100%': { transform: 'rotateZ(0)' },
});

const atlaskitCssStyles = cssMap({
	pressableStyles: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		height: '46px',
		width: '46px',
		backgroundColor: 'transparent',
		borderRadius: token('radius.xlarge'),
	},
	pressableStylesRefreshed: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		height: '48px',
		width: '48px',
		backgroundColor: 'transparent',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderRadius: '16px',
	},
	boxStyles: {
		maxWidth: '46px',
		position: 'relative',
		borderRadius: token('radius.xlarge'),

		'&::before': {
			transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
			position: 'absolute',
			content: "''",
			// @ts-expect-error
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			boxShadow: '0px 4px 4px 0px #00000040',
			borderRadius: token('radius.xlarge'),
			height: '46px',
			width: '46px',
			backgroundColor: token('color.background.brand.boldest'),
		},
		'&::after': {
			transition: 'transform 0.2s ease-in-out',
			position: 'absolute',
			backgroundImage:
				'conic-gradient(from 180deg at 50% 50%, #FFA900 -52.2deg, #0065FF 89.76deg, #0469FF 145.8deg, #BF63F3 262.8deg, #FFA900 307.8deg, #0065FF 449.76deg);',
			content: "''",
			zIndex: -1,
			width: `100%`,
			height: `100%`,
			// @ts-expect-error
			top: '-1px',
			// @ts-expect-error
			left: '-1px',
			// @ts-expect-error
			borderRadius: `calc(${token('radius.xlarge')} + 1px)`,
			// @ts-expect-error
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			padding: '1px',
		},
		'&:hover::after': {
			transform: 'scale(1.1)',
		},
		'&:hover::before': {
			transform: 'scale(1.1)',
			backgroundColor: token('color.background.brand.boldest.hovered'),
		},
	},
	boxStylesRefreshed: {
		width: '48px',
		height: '48px',
		flexShrink: 0,
		position: 'relative',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderRadius: '16px',
		backgroundColor: token('color.background.neutral.bold'),
		// @ts-expect-error
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		boxShadow: '0 0 1px 0 rgba(30, 31, 33, 0.31), 0 8px 12px 0 rgba(30, 31, 33, 0.15)',
		transition: 'transform 0.2s ease-in-out',

		'&:hover': {
			transform: 'scale(1.125)',
			transition: 'transform 0.1s ease-in-out',
			cursor: 'pointer',
		},
		'&:active': {
			transform: 'scale(1.0625)',
		},
	},
	wrapperStylesDynamic: {
		animationDuration: `${ANIMATION_DURATION}s`,
		animationIterationCount: '1',
		animationTimingFunction: 'linear',
		animationName: buttonAnimation,
	},
});

export type AIButtonProps = {
	isDisabled?: boolean;
	onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	ref?: React.Ref<HTMLButtonElement>;
};

export const AIButton = forwardRef<HTMLButtonElement, AIButtonProps>(
	(
		{ onClick, isDisabled = false, onMouseEnter, onMouseLeave }: AIButtonProps,
		ref: Ref<HTMLButtonElement>,
	) => {
		return (
			<div css={atlaskitCssStyles.boxStyles}>
				<Pressable
					testId="platform-ai-button"
					xcss={atlaskitCssStyles.pressableStyles}
					onClick={onClick}
					isDisabled={isDisabled}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					ref={ref}
					interactionName="rovo-button-click"
				>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<VisuallyHidden> Rovo Button </VisuallyHidden>
					<RovoLogo />
				</Pressable>
			</div>
		);
	},
);

export const AIButtonRefreshed = forwardRef<HTMLButtonElement, AIButtonProps>(
	(
		{ onClick, isDisabled = false, onMouseEnter, onMouseLeave }: AIButtonProps,
		ref: Ref<HTMLButtonElement>,
	) => {

		return (
			<div css={atlaskitCssStyles.boxStylesRefreshed}>
				<Pressable
					testId="platform-ai-button"
					xcss={atlaskitCssStyles.pressableStylesRefreshed}
					onClick={onClick}
					isDisabled={isDisabled}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					ref={ref}
					interactionName="rovo-button-click"
				>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<VisuallyHidden> Rovo Button </VisuallyHidden>
					<RovoLogoRefreshed />
				</Pressable>
			</div>
		);
	},
);
